import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that provides inline AJAX search for blog articles.
 * Uses Shopify's predictive search API to fetch results without page redirect.
 *
 * The grid element lives outside this component (owned by blog-posts-list),
 * so we reference it via a data-grid-id attribute instead of a ref.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset/clear button.
 * @property {HTMLElement} noResults - The no results message.
 * @extends {Component<Refs>}
 */
class BlogSearch extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string} */
  #originalGridHTML = '';

  /** @type {string} */
  #blogHandle = '';

  /** @type {HTMLElement | null} */
  #grid = null;

  connectedCallback() {
    super.connectedCallback();
    this.#blogHandle = this.dataset.blogHandle || '';

    const gridId = this.dataset.gridId;
    if (gridId) {
      this.#grid = document.getElementById(gridId);
      if (this.#grid) {
        this.#originalGridHTML = this.#grid.innerHTML;
      }
    }
  }

  /**
   * Debounced search handler triggered on input.
   */
  onSearchInput = debounce(() => {
    const query = this.refs.searchInput.value.trim();

    if (!query.length) {
      this.#resetSearch();
      return;
    }

    this.refs.resetButton.hidden = false;
    this.#fetchResults(query);
  }, 300);

  /**
   * Handle clear button click.
   */
  onResetClick = () => {
    this.#resetSearch();
    this.refs.searchInput.focus();
  };

  /**
   * Handle Escape key to clear search.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
    }
  };

  /**
   * Fetch articles from Shopify's predictive search API.
   * @param {string} query
   */
  async #fetchResults(query) {
    const abortController = this.#createAbortController();

    try {
      const url = new URL('/search/suggest.json', location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', '10');

      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) return;

      const data = await response.json();
      const articles = data.resources?.results?.articles || [];

      // Filter to current blog
      const filtered = this.#blogHandle
        ? articles.filter((a) => a.url.includes(`/blogs/${this.#blogHandle}/`))
        : articles;

      this.#renderResults(filtered);
    } catch (error) {
      if (error.name === 'AbortError') return;
      throw error;
    }
  }

  /**
   * Render search results into the grid.
   * @param {Array} articles
   */
  #renderResults(articles) {
    if (!this.#grid) return;

    // Hide pagination
    this.#togglePagination(false);

    if (!articles.length) {
      this.#grid.innerHTML = '';
      this.refs.noResults.hidden = false;
      return;
    }

    this.refs.noResults.hidden = true;
    this.#grid.innerHTML = articles.map((article) => this.#buildCardHTML(article)).join('');
  }

  /**
   * Build blog card HTML matching the co-component-blog-card structure.
   * @param {object} article - Article object from predictive search API.
   * @returns {string}
   */
  #buildCardHTML(article) {
    const image = article.image
      ? `<div class="blog-post-card__image-container">
          <a href="${article.url}">
            <img
              src="${article.image}"
              loading="lazy"
              alt="${this.#escapeHTML(article.title)}"
              class="border-style blog-post-card__image blog-post-card__image--small"
              style="--border-width: 0px; --border-style: none; --border-color: transparent; --border-radius: 4px;"
            >
          </a>
        </div>`
      : '';

    // Format date
    let dateHTML = '';
    if (article.published_at) {
      const date = new Date(article.published_at);
      const formatted = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      dateHTML = `<div class="blog-post-details spacing-style justify-left"
        style="--padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px;">
        <span><time datetime="${article.published_at}">${formatted}</time></span>
        ${article.author ? `<span> · </span><span>${this.#escapeHTML(article.author)}</span>` : ''}
      </div>`;
    }

    // Excerpt — truncate body text to ~30 words
    let excerptHTML = '';
    if (article.body) {
      const stripped = article.body.replace(/<[^>]*>/g, '');
      const words = stripped.split(/\s+/).slice(0, 30).join(' ');
      const truncated = words + (stripped.split(/\s+/).length > 30 ? '...' : '');
      excerptHTML = `<div class="blog-post-card__content-text spacing-style text-block rte"
        style="--padding-block-start: 16px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px;">
        ${this.#escapeHTML(truncated)}
      </div>`;
    }

    return `<div class="blog-post-item">
      <div class="blog-post-card" style="--text-align: left">
        ${image}
        <div class="blog-post-card__content">
          <a href="${article.url}">
            <div class="spacing-style text-block h4 text-block--align-left"
              style="--padding-block-start: 0px; --padding-block-end: 10px; --padding-inline-start: 0px; --padding-inline-end: 0px; --color: var(--color-foreground-heading); --width: 100%;">
              ${this.#escapeHTML(article.title)}
            </div>
          </a>
          ${dateHTML}
          ${excerptHTML}
        </div>
      </div>
    </div>`;
  }

  /**
   * Escape HTML special characters.
   * @param {string} str
   * @returns {string}
   */
  #escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Toggle pagination visibility.
   * @param {boolean} show
   */
  #togglePagination(show) {
    if (!this.#grid) return;
    const parent = this.#grid.parentElement;
    if (!parent) return;
    const paginationEls = parent.querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"]');
    paginationEls.forEach((el) => {
      /** @type {HTMLElement} */ (el).hidden = !show;
    });
  }

  /**
   * Reset search to original state.
   */
  #resetSearch() {
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.refs.noResults.hidden = true;

    if (this.#grid) {
      this.#grid.innerHTML = this.#originalGridHTML;
    }

    this.#togglePagination(true);

    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  /**
   * Create a new AbortController, aborting any in-flight request.
   * @returns {AbortController}
   */
  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
